import { Button, Descriptions, Form, Input, Radio, Tooltip } from "antd";
import { ArrowLeftOutlined, StopOutlined, CopyOutlined } from '@ant-design/icons';
import TextArea from "antd/lib/input/TextArea";
import { enmEvaluationMethods, enmEvaluationStatus, evaluationMethodsOptions, IDamageCase } from "../../baseInfo/damageCase/interfaces";
import { Core } from "../../components/infrastructure/core";
import { enmEntityState, ICallOptions, IEntity, IResultSet } from "../../components/infrastructure/models";
import { EvaluationComponent, IEvaluationComponentFormProps, IEvaluationComponentProps, IEvaluationComponentStates, IEvaluationInitialData } from "./evaluationComponent";
import dayjs from 'dayjs';
import { IAttachment } from "../../baseInfo/attachment/interfaces";
import { AttachmentUploader } from "../../baseInfo/attachment/attachmentUploader";
import React from "react";
import { IDamageType } from "../../baseInfo/damageType/interfaces";
import { Meet } from "../../components/jitsi/meet";
import { AttachmentDownloader } from "../../baseInfo/attachment/attachmentDownloader";
import HelperPopover from "../../components/helperPopover";
import { enmContactStatus } from "./interfaces";

type LayoutType = Parameters<typeof Form>[0]['layout'];

export const contactStatusOptions = [
    { title: 'رد خسارت', value: enmContactStatus.Unacceptable },
    { title: 'تأیید خسارت', value: enmContactStatus.Acceptable },
    { title: 'عدم برقراری تماس تصویری', value: enmContactStatus.Failed },
    { title: 'تغییر روش ارزیابی به مراجعه حضوری', value: enmContactStatus.EvaluationMethodToPolicyholderInPerson }
];

export interface IMakingContact extends IEntity {
    DamageCaseEvaluatorId: number;
    ContactStatus: enmContactStatus;
    DamageTypeId: number;
    DamageType: IDamageType;
    Description?: string;
    EvaluationMakingContactAttachments: IAttachment[];
}

export interface IMakingContactStates extends IEvaluationComponentStates<IMakingContact> {

}

export interface IMakingContactProps extends IEvaluationComponentProps {

}

interface IMakingContactInitialData extends IEvaluationInitialData {

}

export interface IMakingContactFormProps extends IEvaluationComponentFormProps<IMakingContact> {
    onChangeContactStatus: (e: any) => void;
    onChangeDescription: (value?: any) => void;
    onChangeAttachments: (attachments: IAttachment[]) => void;
}

const sampleDescriptions = [
    { type: enmEvaluationMethods.VideoCall, message: "بیمه گزار/مشتری در زمان مقرر در جلسه آنلاین حاضر نشد" },
    //{ type: enmEvaluationMethods.EvaluatorInPerson, message: "بیمه گزار/مشتری در محل حضور نداشت" },
    { type: enmEvaluationMethods.PolicyholderInPerson, message: "بیمه گزار/مشتری در زمان مقرر حاضر نشد" },
    //{ type: enmEvaluationMethods.PolicyholderInPersonToInsurerBranch, message: "بیمه گزار/مشتری در زمان مقرر در شعبه حاضر شد" }
];

export const MakingContactForm = (props: IMakingContactFormProps) => {
    const { EvaluationMethod } = props.damageCase;
    const formItemLayout = {
        labelCol: EvaluationMethod === enmEvaluationMethods.VideoCall ?
            { xxl: 6, xl: 6, lg: 6, md: 6, sm: 6, xs: 6 } : { xxl: 8, xl: 10, lg: 12, md: 12, sm: 12, xs: 12 },
        wrapperCol: { xxl: 10, xl: 14, lg: 12, md: 12, sm: 12, xs: 12 }
    };

    const [formLayout] = React.useState<LayoutType>('horizontal');
    const [form] = Form.useForm();

    React.useEffect(() => {
        let currentEntity = props.currentEntity as any;
        form.setFieldsValue(currentEntity);
    }, [props, form]);

    const descriptionPopoverContent =
        <div>
            {sampleDescriptions.filter(a => a.type === EvaluationMethod).map(a =>
                <p
                    key={a.message}
                    className="helperDescription"
                    onClick={() => {
                        const desc = form.getFieldValue('Description');
                        const value = (desc ? desc + '\n' : '') + a.message;
                        props.onChangeDescription && props.onChangeDescription(value);
                    }} >
                    {a.message}
                </p>)}
        </div>;
    const methodTypeTitle = evaluationMethodsOptions.find(m => props.currentEntity.ContactStatus === enmContactStatus.EvaluationMethodToPolicyholderInPerson || m.value === EvaluationMethod)?.name;
    return (
        <Form
            scrollToFirstError
            form={form}
            {...formItemLayout}
            layout={formLayout}
            onFinish={async (values) => {
                props.onSubmit && await props.onSubmit(values);
            }}
            onReset={() => {
                props.onClose && props.onClose(this);
            }}
        >
            <Form.Item
                label={`نتیجه ${methodTypeTitle}`}
                name={'ContactStatus'}
                key={'ContactStatus'}
                rules={[{ required: true, message: 'پاسخ اجباری است' }]}
            >
                <Radio.Group key={'ContactStatus'} onChange={props.onChangeContactStatus} disabled={props.disabled}>
                    {contactStatusOptions.filter(s => EvaluationMethod === enmEvaluationMethods.VideoCall || props.currentEntity.ContactStatus === enmContactStatus.EvaluationMethodToPolicyholderInPerson || s.value !== enmContactStatus.EvaluationMethodToPolicyholderInPerson)
                        .map(s =>
                        <Radio key={s.value} value={s.value}>{s.value === enmContactStatus.Failed ? (EvaluationMethod === enmEvaluationMethods.VideoCall ? s.title : 'عدم حضور بیمه گزار/مشتری') : s.title}</Radio>
                    )}
                </Radio.Group>
            </Form.Item>

            <Form.Item
                label={'توضیحات'}
                name={'Description'}
                key={'Description'}
                rules={[{
                    required: props.currentEntity.ContactStatus !== enmContactStatus.Acceptable,
                    message: 'توضیحات الزامی است',
                }]}
                extra={
                    props.disabled ? <></> : <HelperPopover descriptionPopoverContent={descriptionPopoverContent} title="نمونه توضیحات" placement="right" style={{ marginRight: 1, verticalAlign: 'top', position: "absolute", top: 0, left: 0 }} />
                }
            >
                <TextArea
                    disabled={props.disabled}
                    onChange={e => props.onChangeDescription(e.target.value)}
                    style={{ width: props.disabled ? "100%" : "calc(100% - 34px)" }} />
            </Form.Item>

            {props.currentEntity.ContactStatus === enmContactStatus.Acceptable &&
                <Form.Item
                    label={'مستندات'}
                    name={'Attachments'}
                    key={'Attachments'}
                    rules={[{
                        required: true,
                        validator: () => {
                            if (props.currentEntity.ContactStatus !== enmContactStatus.Failed && props.currentEntity.EvaluationMakingContactAttachments.length === 0)
                                return Promise.reject(new Error('آپلود مستندات الزامی است'));
                            return Promise.resolve();
                        }
                    }]}
                >
                    {props.disabled === true
                        ? <AttachmentDownloader attachments={props.currentEntity.EvaluationMakingContactAttachments} />
                        : <AttachmentUploader
                            whiteList='image/png,image/jpg,image/jpeg,application/pdf,video/mp4'
                            attachments={props.currentEntity.EvaluationMakingContactAttachments}
                            onChange={props.onChangeAttachments}
                            disabled={props.disabled} />
                    }
                </Form.Item>
            }
            {props.disabled === true ? <></> :
                <Form.Item wrapperCol={{ dir: 'ltr' }} style={{ zIndex: 1000, borderTop: '1px solid', borderColor: '#e5e5e5', marginTop: 10 }}>
                    <Button type="primary" htmlType="submit" icon={<ArrowLeftOutlined />} loading={props.loading} style={{ width: "100px", marginTop: 10, marginBottom: 10 }}>
                        بعدی
                    </Button>

                    <Button type="ghost" htmlType="reset" icon={<StopOutlined />} loading={props.loading} style={{ width: "100px", marginTop: 10, marginBottom: 10, marginLeft: 10, background: '#fff' }}>
                        لغو
                    </Button>
                </Form.Item>
            }
        </Form>
    );
}

export class MakingContact extends EvaluationComponent<IMakingContact, IMakingContactProps, IMakingContactStates>{
    onInitializing() {

        this.defaultEntity = {
            EvaluationMakingContactAttachments: [],
            ConfirmPossessionTransition: this.props.damageCase.ConfirmPossessionTransition,
            DamageTypeId: this.props.damageCase.DamageTypeId,
            DamageType: this.props.damageCase.DamageType,
        } as any;

        const title = evaluationMethodsOptions.find(m => m.value === this.props.damageCase.EvaluationMethod)!.makingContactCommandTitle;
        this.defaultState = {
            title,
            damageTypes: [],
            allDamageCases: []
        } as any;

        this.onInitialDataFetching = (params: any) => {
            params.damageCaseId = this.props.damageCase.Id;
            return params;
        };

        this.onInitialDataFetched = (result: IResultSet<any>) => {
            let makeingContactInitialData = result.Data as IMakingContactInitialData;

            this.setState({
                loading: false,
                evaluationSteps: makeingContactInitialData.EvaluationSteps,
                damageCaseDocumentTypeAttachments: makeingContactInitialData.DamageCaseDocumentTypeAttachments,
                policyCert: makeingContactInitialData.PolicyCert,
                allDamageCases: makeingContactInitialData.AllDamageCases
            });
        };
    }
    extraHeaderDescriptions = () => {
        let contactSettings = this.state.evaluationSteps.filter(s => s.Substatus === enmEvaluationStatus.ContactSetting);
        let contactSetting = contactSettings[contactSettings.length - 1] as any;
        const evaluationMethod = this.props.damageCase?.EvaluationMethod;

        if (!contactSetting /*|| evaluationMethod === enmEvaluationMethods.EvaluatorInPerson*/)
            return (<></>);

        let title = evaluationMethodsOptions.find(m => m.value === evaluationMethod)?.name;
        let contactDate = dayjs(contactSetting.ContactDate).calendar('jalali').locale('fa').format("YYYY/MM/DD HH:mm");

        return (
            <>
                <Descriptions.Item label={"زمان " + title}>{contactDate}</Descriptions.Item>
                {evaluationMethod === enmEvaluationMethods.VideoCall &&
                    <Descriptions.Item label="لینک تماس" >
                        <Input
                            disabled
                            defaultValue={contactSetting.ConferenceUrl}
                            ref={node => {
                                if (node) {
                                    node.input.style.padding = "2px";
                                    node.input.style.margin = "0";
                                }
                            }}
                            addonBefore={
                                <Tooltip title="کپی لینک" >
                                    <CopyOutlined onClick={async () => {
                                        if (contactSetting.ConferenceUrl)
                                            await navigator.clipboard.writeText(contactSetting.ConferenceUrl);
                                    }} />
                                </Tooltip>
                            } />
                    </Descriptions.Item>
                }
            </>
        );
    };
    extraHeaderButtons = () => {
        const evaluationMethod = this.props.damageCase?.EvaluationMethod;
        if (evaluationMethod !== enmEvaluationMethods.VideoCall)
            return (<></>);
        let contactSettings = this.state.evaluationSteps.filter(s => s.Substatus === enmEvaluationStatus.ContactSetting);
        let contactSetting = contactSettings[contactSettings.length - 1] as any;
        if (!contactSetting)
            return (<></>);
        return (
            contactSetting.ConferenceUrl.indexOf("https://meet.jit.si/") > -1
                ? <Button type={"primary"} key="VideoCall" onClick={() => this.videoCall(contactSetting.ConferenceUrl)} >تماس تصویری</Button>
                : <a href={contactSetting.ConferenceUrl} target="_blank"><Button type={"primary"} key="VideoCall">تماس تصویری</Button></a>
        );
    }
    videoCall = (conferenceUrl: string) => {
        this.addLookupForm("Meet",
            <Meet
                userName={this.userInfo?.userFullName!}
                roomName={conferenceUrl.replace("https://meet.jit.si/", "")}
                onClosed={async (mediaBlobUrl?: string | null, fileName?: string) => {
                    let currentEntity = this.state.currentEntity;
                    if (mediaBlobUrl && fileName) {
                        const blob = (await fetch(mediaBlobUrl).then(r => r.blob())) as any;
                        const file = new File([blob], fileName, { type: "" })
                        currentEntity.EvaluationMakingContactAttachments.push({
                            RowGuid: Core.newId(),
                            State: enmEntityState.Added,
                            File: file,
                            Title: file.name,
                            Description: 'فایل تماس تصویری',
                            Path: undefined
                        } as any);
                        this.setState({ currentEntity });
                    }
                    this.destroyLookupForms("Meet");
                    this.setState({ displayLookupForm: undefined, currentEntity });
                }}
            />
        );
    }
    onChangeContactStatus = (e: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.ContactStatus = e.target?.value;
        this.setState({ currentEntity });
    }
    onChangeDescription = (value?: any) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.Description = value;
        this.setState({ currentEntity });
    }
    onChangeAttachments = (attachments: IAttachment[]) => {
        let currentEntity = this.state.currentEntity;
        currentEntity.EvaluationMakingContactAttachments = attachments;
        this.setState({ currentEntity });
    }
    onSubmit = async () => {
        if (!this.state.currentEntity)
            return;

        let currentEntity = this.state.currentEntity;

        let formData = new FormData();

        let evaluation = {
            RowGuid: currentEntity.RowGuid,
            DamageCaseEvaluatorId: currentEntity.DamageCaseEvaluatorId,
            ContactStatus: currentEntity.ContactStatus,
            Description: currentEntity.Description,
            EvaluationMakingContactAttachments: currentEntity.EvaluationMakingContactAttachments.map(attachment => {
                formData.append('Attachments', (attachment as any).File);
                return { RowGuid: attachment.RowGuid, Title: attachment.Title, Description: attachment.Description }
            })
        };
        formData.append('Evaluation', JSON.stringify(evaluation));

        let fetchOpt: ICallOptions = {
            url: "damagecaseevaluator/makingcontact",
            data: formData,
            contentType: false,
            headers: [{ key: "encType", value: "multipart/form-data" }],
            onSuccess: (result: IResultSet<any>) => {
                if (result.ValidationState.Successful) {
                    if ([enmContactStatus.Acceptable, enmContactStatus.Failed].indexOf(currentEntity.ContactStatus) > -1)
                        this.props.nextStatus(result.Data as IDamageCase);
                    else
                        this.props.onClose && this.props.onClose(this);
                }
            }
        };
        return await this.callApi(fetchOpt);
    }
    renderBody() {

        return (
            <MakingContactForm
                key="MakingContactForm"
                loading={this.state.loading}
                currentEntity={this.state.currentEntity}
                damageCase={this.props.damageCase}
                onClose={this.props.onClose}
                onSubmit={this.onSubmit}
                onChangeContactStatus={this.onChangeContactStatus}
                onChangeDescription={this.onChangeDescription}
                onChangeAttachments={this.onChangeAttachments}
                damageCaseDocumentTypeAttachments={this.state.damageCaseDocumentTypeAttachments}
            />
        );
    }
}